import React, { useState, useRef } from 'react';
import * as tbrApi from 'src/lib/tbrApi';

const DEV_MODE = process.env.NODE_ENV === 'development';
// Deep-clone data, apply a mutation, return the result — used to stub API responses locally.
function devStub(data, mutate) {
    const clone = JSON.parse(JSON.stringify(data));
    mutate(clone);
    return clone;
}

const LOCATION_PRESETS = ['Kim\'s House', 'Jemma\'s House', 'Jordyn\'s House', 'Jaimie\'s House', 'Jessica\'s House'];

async function searchOpenLibrary(query) {
    if (!query.trim()) return [];
    try {
        const res = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=6&fields=title,author_name,cover_i`);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.docs || []).map(d => ({
            title: d.title,
            author: (d.author_name || [])[0] || '',
            coverId: d.cover_i || null,
        }));
    } catch { return []; }
}

function CoverImg({ coverId, size = 'M', className }) {
    if (!coverId) return null;
    return (
        <img
            className={className}
            src={`https://covers.openlibrary.org/b/id/${coverId}-${size}.jpg`}
            alt=""
        />
    );
}

function StarPicker({ value, onChange }) {
    return (
        <div className="star-picker">
            {[1, 2, 3, 4, 5].map(n => (
                <button
                    key={n}
                    type="button"
                    className={'star-btn' + (n <= value ? ' is-on' : '')}
                    onClick={() => onChange(n)}
                >★</button>
            ))}
        </div>
    );
}

function EditBookModal({ current, onSave, onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(current || null);
    const inputRef = useRef(null);

    async function runSearch() {
        if (!query.trim()) return;
        setSearching(true);
        setResults(await searchOpenLibrary(query));
        setSearching(false);
    }

    return (
        <div className="modal-scrim" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <header className="modal-hd">
                    <h2>Set current book</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>
                <div className="modal-body">
                    {!selected ? (
                        <div className="gate-field">
                            <label className="gate-label">Search Open Library</label>
                            <div className="search-row">
                                <input
                                    ref={inputRef}
                                    className="gate-input"
                                    type="text"
                                    placeholder="Title or author…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                                    autoFocus
                                />
                                <button type="button" className="search-btn" onClick={runSearch} disabled={!query.trim()}>Search →</button>
                            </div>
                            {searching && <div className="search-status">Searching…</div>}
                            {results.length > 0 && (
                                <ul className="search-results-list">
                                    {results.map((r, i) => (
                                        <li key={i} className="search-result-item" onClick={() => setSelected(r)}>
                                            <CoverImg coverId={r.coverId} size="S" className="search-result-cover" />
                                            <div className="search-result-info">
                                                <div className="search-result-title">{r.title}</div>
                                                {r.author && <div className="search-result-author">{r.author}</div>}
                                            </div>
                                            <span className="search-result-pick">select →</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ) : (
                        <div className="search-selected">
                            <div className="search-selected-label">Selected</div>
                            <div className="search-selected-title">{selected.title}</div>
                            {selected.author && <div className="search-selected-author">{selected.author}</div>}
                            <button type="button" className="search-selected-clear" onClick={() => { setSelected(null); setResults([]); }}>change ×</button>
                        </div>
                    )}
                </div>
                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn-primary" disabled={!selected} onClick={() => onSave(selected)}>Set book</button>
                </footer>
            </div>
        </div>
    );
}

function EditMeetingModal({ meeting, onSave, onClose }) {
    const [date, setDate] = useState(meeting?.date || '');
    const [location, setLocation] = useState(meeting?.location || '');
    const [locMode, setLocMode] = useState(
        meeting?.location && !LOCATION_PRESETS.includes(meeting.location) ? 'manual' : 'preset'
    );

    function handleSave() {
        onSave({ date: date || null, location: location || null });
    }

    return (
        <div className="modal-scrim" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <header className="modal-hd">
                    <h2>Edit next meeting</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>
                <div className="modal-body">
                    <div className="gate-field">
                        <label className="gate-label">Date</label>
                        <input
                            className="gate-input"
                            type="datetime-local"
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="gate-field">
                        <label className="gate-label">Location</label>
                        <div className="toggle-row" style={{ marginBottom: 10 }}>
                            <button type="button" className={'toggle-btn' + (locMode === 'preset' ? ' is-on' : '')} onClick={() => { setLocMode('preset'); setLocation(''); }}>Member's house</button>
                            <button type="button" className={'toggle-btn' + (locMode === 'manual' ? ' is-on' : '')} onClick={() => setLocMode('manual')}>Other</button>
                        </div>
                        {locMode === 'preset' ? (
                            <div className="bc-location-presets">
                                {LOCATION_PRESETS.map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={'bc-location-preset' + (location === p ? ' is-on' : '')}
                                        onClick={() => setLocation(p)}
                                    >{p}</button>
                                ))}
                            </div>
                        ) : (
                            <input
                                className="gate-input"
                                type="text"
                                placeholder="e.g. Café on Smith St"
                                value={location}
                                onChange={e => setLocation(e.target.value)}
                            />
                        )}
                    </div>
                </div>
                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn-primary" onClick={handleSave}>Save</button>
                </footer>
            </div>
        </div>
    );
}

function RatingModal({ bookTitle, onSubmit, onSkip }) {
    const [stars, setStars] = useState(0);
    const [comment, setComment] = useState('');

    return (
        <div className="modal-scrim">
            <div className="modal">
                <header className="modal-hd">
                    <h2>Rate the book</h2>
                </header>
                <div className="modal-body">
                    <p className="bc-prompt-text">How was <em>{bookTitle}</em>?</p>
                    <div className="gate-field">
                        <label className="gate-label">Stars</label>
                        <StarPicker value={stars} onChange={setStars} />
                    </div>
                    <div className="gate-field">
                        <label className="gate-label">Comment (optional)</label>
                        <input
                            className="gate-input"
                            type="text"
                            placeholder="Any thoughts?"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                        />
                    </div>
                </div>
                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onSkip}>Skip</button>
                    <button type="button" className="btn-primary" disabled={stars === 0} onClick={() => onSubmit(stars, comment)}>Submit rating</button>
                </footer>
            </div>
        </div>
    );
}

function NominationModal({ recommendation, onSubmit, onSkip, onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);

    async function runSearch() {
        if (!query.trim()) return;
        setSearching(true);
        setResults(await searchOpenLibrary(query));
        setSearching(false);
    }

    return (
        <div className="modal-scrim">
            <div className="modal">
                <header className="modal-hd">
                    <h2>Pick your next read</h2>
                </header>
                <div className="modal-body">
                    <p className="bc-prompt-text">What book would you like for book club?</p>

                    {recommendation && !selected && (
                        <div className="bc-recommendation">
                            <div className="bc-recommendation-label">From your wishlists</div>
                            <div className="bc-recommendation-card" onClick={() => setSelected({ title: recommendation.title, author: recommendation.author, coverId: recommendation.coverId || null })}>
                                <div className="bc-recommendation-info">
                                    <div className="bc-recommendation-title">{recommendation.title}</div>
                                    {recommendation.author && <div className="bc-recommendation-author">{recommendation.author}</div>}
                                    <div className="bc-recommendation-wanted">On {recommendation.wantedBy.length} wishlists · {recommendation.wantedBy.join(', ')}</div>
                                </div>
                                <span className="search-result-pick">nominate →</span>
                            </div>
                            <div className="bc-recommendation-divider">or search below</div>
                        </div>
                    )}

                    {!selected ? (
                        <div className="gate-field">
                            <div className="search-row">
                                <input
                                    className="gate-input"
                                    type="text"
                                    placeholder="Title or author…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                                    autoFocus={!recommendation}
                                />
                                <button type="button" className="search-btn" onClick={runSearch} disabled={!query.trim()}>Search →</button>
                            </div>
                            {searching && <div className="search-status">Searching…</div>}
                            {results.length > 0 && (
                                <ul className="search-results-list">
                                    {results.map((r, i) => (
                                        <li key={i} className="search-result-item" onClick={() => setSelected(r)}>
                                            <CoverImg coverId={r.coverId} size="S" className="search-result-cover" />
                                            <div className="search-result-info">
                                                <div className="search-result-title">{r.title}</div>
                                                {r.author && <div className="search-result-author">{r.author}</div>}
                                            </div>
                                            <span className="search-result-pick">select →</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ) : (
                        <div className="search-selected">
                            <div className="search-selected-label">Your nomination</div>
                            <div className="search-selected-title">{selected.title}</div>
                            {selected.author && <div className="search-selected-author">{selected.author}</div>}
                            <button type="button" className="search-selected-clear" onClick={() => { setSelected(null); setResults([]); }}>change ×</button>
                        </div>
                    )}
                </div>
                <footer className="modal-actions">
                    {onClose && <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>}
                    <button type="button" className="btn-secondary" onClick={onSkip}>Skip</button>
                    <button type="button" className="btn-primary" disabled={!selected} onClick={() => onSubmit(selected)}>Nominate</button>
                </footer>
            </div>
        </div>
    );
}

function RunoffModal({ nominations, caller, onVote, onClose }) {
    const [chosen, setChosen] = useState(null);

    return (
        <div className="modal-scrim">
            <div className="modal">
                <header className="modal-hd">
                    <h2>Runoff vote</h2>
                </header>
                <div className="modal-body">
                    <p className="bc-prompt-text">Everyone picked something different. Choose your favourite from the list:</p>
                    <ul className="bc-runoff-list">
                        {nominations.map((n, i) => (
                            <li
                                key={i}
                                className={'bc-runoff-item' + (chosen === i ? ' is-chosen' : '')}
                                onClick={() => setChosen(i)}
                            >
                                <CoverImg coverId={n.coverId} size="S" className="bc-runoff-cover" />
                                <div className="bc-runoff-info">
                                    <div className="bc-runoff-title">{n.title}</div>
                                    {n.author && <div className="bc-runoff-author">{n.author}</div>}
                                    <div className="bc-runoff-nom">Nominated by {n.nominatedBy}</div>
                                </div>
                                <span className={'bc-runoff-check' + (chosen === i ? ' is-chosen' : '')}>✓</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <footer className="modal-actions">
                    {onClose && <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>}
                    <button type="button" className="btn-primary" disabled={chosen === null} onClick={() => onVote(chosen)}>Cast vote</button>
                </footer>
            </div>
        </div>
    );
}

const MEMBERS = tbrApi.TBR_USERS.filter(u => !u.admin).map(u => u.name);

function AdminVoteModal({ vote, onClose }) {
    const phase = vote.phase;

    // Map nomination by nominator name for quick lookup
    const nomByMember = {};
    (vote.nominations || []).forEach(n => { nomByMember[n.nominatedBy] = n; });

    // Map which nomination each member voted for in runoff
    const runoffVoteByMember = {};
    if (phase === 'runoff') {
        (vote.nominations || []).forEach(n => {
            (n.votes || []).forEach(voter => { runoffVoteByMember[voter] = n; });
        });
    }

    // Backwards compat: votes created before nominationActed was added fall back to hasActed
    const nominationActed = vote.nominationActed
        || (vote.phase === 'nominating' ? vote.hasActed || [] : []);

    return (
        <div className="modal-scrim" onClick={onClose}>
            <div className="modal bc-admin-modal" onClick={e => e.stopPropagation()}>
                <header className="modal-hd">
                    <h2>Vote status <span className="bc-admin-modal-badge">admin</span></h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>
                <div className="modal-body">

                    {/* Ratings */}
                    {vote.wasFinished && (
                        <div className="bc-admin-section">
                            <div className="bc-admin-section-label">Ratings</div>
                            <table className="bc-admin-table">
                                <tbody>
                                    {MEMBERS.map(name => {
                                        const r = vote.ratings && vote.ratings[name];
                                        return (
                                            <tr key={name} className="bc-admin-row">
                                                <td className="bc-admin-name">{name}</td>
                                                <td className="bc-admin-value">
                                                    {r === undefined
                                                        ? <span className="bc-admin-pending">not yet</span>
                                                        : r === null
                                                            ? <span className="bc-admin-skipped">skipped</span>
                                                            : <span className="bc-admin-rating">
                                                                {'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}
                                                                {r.comment && <em className="bc-admin-comment"> — {r.comment}</em>}
                                                              </span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Nominations */}
                    {(phase === 'nominating' || phase === 'runoff' || phase === 'done') && (
                        <div className="bc-admin-section">
                            <div className="bc-admin-section-label">Nominations</div>
                            <table className="bc-admin-table">
                                <tbody>
                                    {MEMBERS.map(name => {
                                        const acted = nominationActed.includes(name);
                                        const nom = nomByMember[name];
                                        return (
                                            <tr key={name} className="bc-admin-row">
                                                <td className="bc-admin-name">{name}</td>
                                                <td className="bc-admin-value">
                                                    {!acted
                                                        ? <span className="bc-admin-pending">not yet</span>
                                                        : nom
                                                            ? <span className="bc-admin-nomination">
                                                                {nom.title}
                                                                {nom.author && <em className="bc-admin-comment"> by {nom.author}</em>}
                                                              </span>
                                                            : <span className="bc-admin-skipped">skipped</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Runoff votes */}
                    {phase === 'runoff' && (
                        <div className="bc-admin-section">
                            <div className="bc-admin-section-label">Runoff votes</div>
                            <table className="bc-admin-table">
                                <tbody>
                                    {MEMBERS.map(name => {
                                        const voted = vote.hasActed && vote.hasActed.includes(name);
                                        const pick = runoffVoteByMember[name];
                                        return (
                                            <tr key={name} className="bc-admin-row">
                                                <td className="bc-admin-name">{name}</td>
                                                <td className="bc-admin-value">
                                                    {!voted
                                                        ? <span className="bc-admin-pending">not yet</span>
                                                        : pick
                                                            ? <span className="bc-admin-nomination">{pick.title}</span>
                                                            : <span className="bc-admin-skipped">skipped</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

function HistoryModal({ archive, onClose }) {
    const [expanded, setExpanded] = useState(null);
    return (
        <div className="modal-scrim" onClick={onClose}>
            <div className="modal bc-history-modal" onClick={e => e.stopPropagation()}>
                <header className="modal-hd">
                    <h2>Reading history</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>
                <div className="modal-body bc-history-body">
                    {archive.length === 0 && (
                        <p style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>No books archived yet.</p>
                    )}
                    {archive.map((entry, i) => (
                        <div key={i} className="bc-history-entry">
                            <div className="bc-history-row">
                                <div className="bc-history-info">
                                    <div className="bc-history-title">{entry.title}</div>
                                    {entry.author && <div className="bc-history-author">{entry.author}</div>}
                                    <div className="bc-history-date">{entry.finishedAt}</div>
                                </div>
                                <button
                                    type="button"
                                    className="bc-history-rating"
                                    onClick={() => setExpanded(expanded === i ? null : i)}
                                    title="See all ratings"
                                >
                                    {entry.avg != null ? <>★ {entry.avg}</> : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                                </button>
                            </div>
                            {expanded === i && (
                                <ul className="bc-history-ratings-list">
                                    {(entry.ratings || []).map((r, j) => (
                                        <li key={j} className="bc-history-rating-row">
                                            <span className="bc-history-rating-member">{r.member}</span>
                                            <span className="bc-history-rating-stars">{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</span>
                                            {r.comment && <span className="bc-history-rating-comment">"{r.comment}"</span>}
                                        </li>
                                    ))}
                                    {(entry.ratings || []).length === 0 && (
                                        <li style={{ color: 'var(--ink-3)', fontStyle: 'italic', fontSize: 14 }}>No ratings recorded.</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function BookClub({ data, caller, isAdmin, onUpdate }) {
    const token = tbrApi.getStoredToken();
    const empty = !data || (!data.currentBook && !data.nextMeeting?.date && !data.nextMeeting?.location);
    const [editingBook, setEditingBook] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showAdminVote, setShowAdminVote] = useState(false);
    const [newBookStep, setNewBookStep] = useState(null); // 'confirm-finished' | null
    const [changingNom, setChangingNom] = useState(false);
    const [changingRunoff, setChangingRunoff] = useState(false);
    const [forceEndStep, setForceEndStep] = useState(null); // null | 'confirm1' | 'confirm2'

    const { currentBook, nextMeeting, vote, archive } = data || { currentBook: null, nextMeeting: {}, vote: null, archive: [] };

    const myRatingDone = vote?.phase === 'rating' && vote.ratings?.[caller] !== undefined;
    // nominationActed tracks nominations across both rating+nominating phases.
    // Fall back to hasActed for older votes that pre-date this field.
    const myNomActed = vote?.nominationActed
        ? vote.nominationActed.includes(caller)
        : (vote?.phase === 'nominating' && vote?.hasActed?.includes(caller));
    const myRunoffDone = vote?.phase === 'runoff' && vote.hasActed?.includes(caller);

    const needsRating = !isAdmin && vote?.phase === 'rating' && !myRatingDone;
    // Show nomination prompt in rating OR nominating phase, as long as user has rated (or no rating needed)
    const needsNomination = !isAdmin && (vote?.phase === 'nominating' || vote?.phase === 'rating') && !myNomActed;
    const needsRunoff = !isAdmin && vote?.phase === 'runoff' && !myRunoffDone;

    const allNomActed = (vote?.nominationActed?.length || 0) >= 5;
    const allRunoffActed = (vote?.hasActed?.length || 0) >= 5;
    const canChangeNom = !isAdmin && myNomActed && !allNomActed;
    const canChangeRunoff = !isAdmin && myRunoffDone && !allRunoffActed;

    async function handleSaveBook(book) {
        if (DEV_MODE) { onUpdate(devStub(data, d => { d.currentBook = { ...book, setAt: new Date().toISOString().slice(0, 7) }; })); setEditingBook(false); return; }
        const updated = await tbrApi.patchCurrentBook(token, book);
        onUpdate(updated);
        setEditingBook(false);
    }

    async function handleSaveMeeting({ date, location }) {
        if (DEV_MODE) { onUpdate(devStub(data, d => { d.nextMeeting = { date, location }; })); setEditingMeeting(false); return; }
        const updated = await tbrApi.patchMeeting(token, date, location);
        onUpdate(updated);
        setEditingMeeting(false);
    }

    async function handleNewBook(wasFinished) {
        setNewBookStep(null);
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote = { wasFinished: !!wasFinished, phase: wasFinished ? 'rating' : 'nominating', ratings: {}, nominations: [], nominationActed: [], hasActed: [], round: 1, recommendation: null };
            }));
            return;
        }
        const updated = await tbrApi.startNewBook(token, wasFinished);
        onUpdate(updated);
    }

    async function handleRate(stars, comment) {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote.ratings[caller] = { stars, comment };
                if (!d.vote.hasActed.includes(caller)) d.vote.hasActed.push(caller);
            }));
            return;
        }
        const updated = await tbrApi.submitRating(token, stars, comment, false);
        onUpdate(updated);
    }

    async function handleSkipRate() {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote.ratings[caller] = null;
                if (!d.vote.hasActed.includes(caller)) d.vote.hasActed.push(caller);
            }));
            return;
        }
        const updated = await tbrApi.submitRating(token, null, null, true);
        onUpdate(updated);
    }

    async function handleNominate(book) {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote.nominations = d.vote.nominations.filter(n => n.nominatedBy !== caller);
                d.vote.nominations.push({ title: book.title, author: book.author || '', coverId: book.coverId || null, nominatedBy: caller, votes: [caller] });
                if (!d.vote.nominationActed) d.vote.nominationActed = [];
                if (!d.vote.nominationActed.includes(caller)) d.vote.nominationActed.push(caller);
                if (!d.vote.hasActed.includes(caller)) d.vote.hasActed.push(caller);
            }));
            setChangingNom(false);
            return;
        }
        const updated = await tbrApi.submitNomination(token, book, false);
        onUpdate(updated);
        setChangingNom(false);
    }

    async function handleSkipNominate() {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote.nominations = d.vote.nominations.filter(n => n.nominatedBy !== caller);
                if (!d.vote.nominationActed) d.vote.nominationActed = [];
                if (!d.vote.nominationActed.includes(caller)) d.vote.nominationActed.push(caller);
                if (!d.vote.hasActed.includes(caller)) d.vote.hasActed.push(caller);
            }));
            setChangingNom(false);
            return;
        }
        const updated = await tbrApi.submitNomination(token, null, true);
        onUpdate(updated);
        setChangingNom(false);
    }

    async function handleRunoffVote(idx) {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                d.vote.nominations.forEach(n => { n.votes = n.votes.filter(v => v !== caller); });
                if (d.vote.nominations[idx]) d.vote.nominations[idx].votes.push(caller);
                if (!d.vote.hasActed.includes(caller)) d.vote.hasActed.push(caller);
            }));
            setChangingRunoff(false);
            return;
        }
        const updated = await tbrApi.submitRunoffVote(token, idx);
        onUpdate(updated);
        setChangingRunoff(false);
    }

    async function handleConfirmWinner() {
        if (DEV_MODE) {
            onUpdate(devStub(data, d => {
                if (d.vote && d.vote.winner) d.currentBook = { ...d.vote.winner, setAt: new Date().toISOString().slice(0, 7) };
                d.vote = null;
            }));
            return;
        }
        const updated = await tbrApi.confirmWinner(token);
        onUpdate(updated);
    }

    async function handleForceEnd() {
        setForceEndStep(null);
        if (DEV_MODE) { onUpdate(devStub(data, d => { d.vote = null; })); return; }
        const updated = await tbrApi.forceEndVote(token);
        onUpdate(updated);
    }

    const formattedDate = (() => {
        const raw = nextMeeting?.date;
        if (!raw) return null;
        const hasTime = raw.includes('T');
        const d = hasTime ? new Date(raw) : new Date(raw + 'T00:00:00');
        const datePart = d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        if (!hasTime) return datePart;
        const timePart = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
        return `${datePart} at ${timePart}`;
    })();

    return (
        <section className="bc-section">
            <div className="bc-header">
                <div className="bc-eyebrow">Book Club</div>
                <div className="bc-header-actions">
                    <button type="button" className="bc-history-btn" onClick={() => setShowHistory(true)}>History</button>
                    {isAdmin && vote && (
                        <button type="button" className="bc-admin-vote-btn" onClick={() => setShowAdminVote(true)}>
                            Vote status
                        </button>
                    )}
                    <button
                        type="button"
                        className="bc-new-book-btn"
                        disabled={!!vote}
                        onClick={() => setNewBookStep('confirm-finished')}
                    >
                        {vote ? 'Vote in progress' : '+ New Book'}
                    </button>
                </div>
            </div>

            {/* Vote in progress banner */}
            {vote && vote.phase !== 'done' && (
                <div className="bc-vote-banner">
                    <span className="bc-vote-banner-label">
                        {vote.phase === 'rating' && 'Collecting ratings'}
                        {vote.phase === 'nominating' && 'Collecting nominations'}
                        {vote.phase === 'runoff' && 'Runoff vote'}
                    </span>
                    <span className="bc-vote-banner-progress">
                        {(() => {
                            const memberActed = arr => MEMBERS.filter(m => (arr || []).includes(m)).length;
                            if (vote.phase === 'runoff') return `${memberActed(vote.hasActed)} / 5 voted`;
                            if (vote.phase === 'nominating') return `${vote.nominationActed ? vote.nominationActed.length : memberActed(vote.hasActed)} / 5 nominated`;
                            return `${memberActed(vote.hasActed)} / 5 rated`;
                        })()}
                    </span>
                    {canChangeNom && (
                        <button type="button" className="bc-change-vote-btn" onClick={() => setChangingNom(true)}>
                            Change nomination ✎
                        </button>
                    )}
                    {canChangeRunoff && (
                        <button type="button" className="bc-change-vote-btn" onClick={() => setChangingRunoff(true)}>
                            Change vote ✎
                        </button>
                    )}
                    {(vote.nominations?.length || 0) >= 1 && (
                        <button type="button" className="bc-force-end-btn" onClick={() => setForceEndStep('confirm1')}>
                            End vote
                        </button>
                    )}
                </div>
            )}

            {/* Winner ready */}
            {vote?.phase === 'done' && vote.winner && (
                <div className="bc-winner-banner">
                    <div className="bc-winner-label">Next book selected</div>
                    <div className="bc-winner-title">{vote.winner.title}</div>
                    {vote.winner.author && <div className="bc-winner-author">{vote.winner.author}</div>}
                    <button type="button" className="btn-primary" style={{ marginTop: 12 }} onClick={handleConfirmWinner}>
                        Set as current book →
                    </button>
                </div>
            )}

            <div className="bc-body">
                {/* Current book */}
                <div className="bc-current-book">
                    <div className="bc-section-label">Currently reading</div>
                    {currentBook ? (
                        <div className="bc-book-card">
                            <CoverImg coverId={currentBook.coverId} size="M" className="bc-book-cover" />
                            <div className="bc-book-info">
                                <div className="bc-book-title">{currentBook.title}</div>
                                {currentBook.author && <div className="bc-book-author">{currentBook.author}</div>}
                                {currentBook.setAt && <div className="bc-book-since">Since {currentBook.setAt}</div>}
                            </div>
                        </div>
                    ) : (
                        <div className="bc-placeholder-book">
                            <div className="bc-placeholder-cover" />
                            <div className="bc-placeholder-lines">
                                <div className="bc-placeholder-line bc-placeholder-line--title" />
                                <div className="bc-placeholder-line bc-placeholder-line--author" />
                                <div className="bc-placeholder-hint">No book selected yet — press edit to set one</div>
                            </div>
                        </div>
                    )}
                    <button type="button" className="bc-edit-btn" onClick={() => setEditingBook(true)}>edit ✎</button>
                </div>

                {/* Next meeting */}
                <div className="bc-meeting">
                    <div className="bc-section-label">Next meeting</div>
                    {formattedDate ? (
                        <>
                            <div className="bc-meeting-date">{formattedDate}</div>
                            <div className="bc-meeting-location">{nextMeeting?.location || <span className="bc-placeholder-hint">No location set</span>}</div>
                        </>
                    ) : (
                        <div className="bc-placeholder-meeting">
                            <div className="bc-placeholder-line bc-placeholder-line--date" />
                            <div className="bc-placeholder-line bc-placeholder-line--loc" />
                            <div className="bc-placeholder-hint">No meeting scheduled yet — press edit to set a date</div>
                        </div>
                    )}
                    <button type="button" className="bc-edit-btn" onClick={() => setEditingMeeting(true)}>edit ✎</button>
                </div>

                {/* Nominations during vote — hidden from regular users until voting is resolved */}
                {vote && vote.nominations?.length > 0 && (isAdmin || vote.phase === 'done') && (
                    <div className="bc-nominations">
                        <div className="bc-section-label">Nominations</div>
                        <ul className="bc-nom-list">
                            {vote.nominations.map((n, i) => (
                                <li key={i} className="bc-nom-item">
                                    <CoverImg coverId={n.coverId} size="S" className="bc-nom-cover" />
                                    <div className="bc-nom-info">
                                        <div className="bc-nom-title">{n.title}</div>
                                        {n.author && <div className="bc-nom-author">{n.author}</div>}
                                        <div className="bc-nom-meta">{n.nominatedBy} · {n.votes.length} vote{n.votes.length !== 1 ? 's' : ''}</div>
                                        <div className="bc-nom-voters">{n.votes.join(', ')}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Modals */}
            {editingBook && <EditBookModal current={currentBook} onSave={handleSaveBook} onClose={() => setEditingBook(false)} />}
            {editingMeeting && <EditMeetingModal meeting={nextMeeting} onSave={handleSaveMeeting} onClose={() => setEditingMeeting(false)} />}
            {showHistory && <HistoryModal archive={archive || []} onClose={() => setShowHistory(false)} />}
            {showAdminVote && vote && <AdminVoteModal vote={vote} onClose={() => setShowAdminVote(false)} />}

            {newBookStep === 'confirm-finished' && (
                <div className="modal-scrim" onClick={() => setNewBookStep(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <header className="modal-hd">
                            <h2>Start a new book?</h2>
                        </header>
                        <div className="modal-body">
                            <p className="bc-prompt-text">
                                Did the group finish <em>{currentBook?.title || 'the current book'}</em>?
                            </p>
                        </div>
                        <footer className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={() => setNewBookStep(null)}>Cancel</button>
                            <button type="button" className="btn-secondary" onClick={() => handleNewBook(false)}>No — just vote</button>
                            <button type="button" className="btn-primary" onClick={() => handleNewBook(true)}>Yes — rate &amp; vote</button>
                        </footer>
                    </div>
                </div>
            )}

            {forceEndStep === 'confirm1' && (
                <div className="modal-scrim" onClick={() => setForceEndStep(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <header className="modal-hd">
                            <h2>End the vote?</h2>
                        </header>
                        <div className="modal-body">
                            <p className="bc-prompt-text">
                                Anyone who hasn't nominated will be skipped and the vote will resolve with the current nominations. Anyone who hasn't rated the old book will still be asked to rate when they log in.
                            </p>
                        </div>
                        <footer className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={() => setForceEndStep(null)}>Cancel</button>
                            <button type="button" className="bc-force-end-confirm-btn" onClick={() => setForceEndStep('confirm2')}>Yes, end it</button>
                        </footer>
                    </div>
                </div>
            )}

            {forceEndStep === 'confirm2' && (
                <div className="modal-scrim" onClick={() => setForceEndStep(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <header className="modal-hd">
                            <h2>Are you really sure?</h2>
                        </header>
                        <div className="modal-body">
                            <p className="bc-prompt-text">
                                Everyone who hasn't voted will be marked as skipped. This cannot be undone.
                            </p>
                        </div>
                        <footer className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={() => setForceEndStep(null)}>Cancel</button>
                            <button type="button" className="bc-force-end-confirm-btn" onClick={handleForceEnd}>End vote now</button>
                        </footer>
                    </div>
                </div>
            )}

            {needsRating && (
                <RatingModal
                    bookTitle={currentBook?.title || 'the last book'}
                    onSubmit={handleRate}
                    onSkip={handleSkipRate}
                />
            )}
            {(needsNomination || changingNom) && !needsRating && (
                <NominationModal
                    recommendation={vote?.recommendation}
                    onSubmit={handleNominate}
                    onSkip={handleSkipNominate}
                    onClose={changingNom ? () => setChangingNom(false) : null}
                />
            )}
            {(needsRunoff || changingRunoff) && (
                <RunoffModal
                    nominations={vote.nominations}
                    caller={caller}
                    onVote={handleRunoffVote}
                    onClose={changingRunoff ? () => setChangingRunoff(false) : null}
                />
            )}
        </section>
    );
}
